// Utilidades puras para el Dashboard Ejecutivo.
// Bucketing por día/semana/mes, cálculo de deltas y resolución de rangos.

export type Granularity = 'day' | 'week' | 'month';
export type RangeKey = '7d' | '30d' | '90d' | '12m' | 'ytd' | 'all';

export interface DateRange {
  from: Date;
  to: Date;
  prevFrom: Date;
  prevTo: Date;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfWeek(d: Date): Date {
  // Lunes como inicio de semana (ISO)
  const x = startOfDay(d);
  const day = x.getDay(); // 0=domingo
  const diff = (day + 6) % 7;
  x.setDate(x.getDate() - diff);
  return x;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function bucketKey(d: Date, g: Granularity): string {
  const b = g === 'day' ? startOfDay(d) : g === 'week' ? startOfWeek(d) : startOfMonth(d);
  const y = b.getFullYear();
  const m = String(b.getMonth() + 1).padStart(2, '0');
  const day = String(b.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatBucketLabel(key: string, g: Granularity): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (g === 'day') return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
  if (g === 'week') return `Sem ${date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}`;
  return date.toLocaleDateString('es-CO', { month: 'short', year: '2-digit' });
}

export function resolveRange(key: RangeKey, now: Date = new Date()): DateRange {
  const to = new Date(now);
  let from: Date;
  switch (key) {
    case '7d': from = new Date(to); from.setDate(from.getDate() - 7); break;
    case '30d': from = new Date(to); from.setDate(from.getDate() - 30); break;
    case '90d': from = new Date(to); from.setDate(from.getDate() - 90); break;
    case '12m': from = new Date(to); from.setMonth(from.getMonth() - 12); break;
    case 'ytd': from = new Date(to.getFullYear(), 0, 1); break;
    case 'all': from = new Date(2020, 0, 1); break;
  }
  const span = to.getTime() - from.getTime();
  const prevTo = new Date(from);
  const prevFrom = new Date(from.getTime() - span);
  return { from, to, prevFrom, prevTo };
}

export function defaultGranularity(range: RangeKey): Granularity {
  if (range === '7d' || range === '30d') return 'day';
  if (range === '90d' || range === 'ytd') return 'week';
  return 'month';
}

export interface Bucket {
  key: string;
  label: string;
  value: number;
  cumulative?: number;
}

export function bucketRows<T>(
  rows: T[],
  dateField: keyof T,
  range: DateRange,
  g: Granularity,
  weight: (row: T) => number = () => 1,
): Bucket[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const raw = row[dateField] as unknown as string | Date | null | undefined;
    if (!raw) continue;
    const d = raw instanceof Date ? raw : new Date(raw);
    if (isNaN(d.getTime())) continue;
    if (d < range.from || d > range.to) continue;
    const key = bucketKey(d, g);
    counts.set(key, (counts.get(key) ?? 0) + weight(row));
  }
  // Rellenar buckets vacíos para curvas continuas
  const buckets: Bucket[] = [];
  const step = g === 'day' ? 1 : g === 'week' ? 7 : 30;
  let cursor = g === 'day' ? startOfDay(range.from) : g === 'week' ? startOfWeek(range.from) : startOfMonth(range.from);
  const end = range.to;
  let acc = 0;
  while (cursor <= end) {
    const key = bucketKey(cursor, g);
    const value = counts.get(key) ?? 0;
    acc += value;
    buckets.push({ key, label: formatBucketLabel(key, g), value, cumulative: acc });
    if (g === 'month') {
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    } else {
      cursor = new Date(cursor);
      cursor.setDate(cursor.getDate() + step);
    }
  }
  return buckets;
}

export function countInRange<T>(rows: T[], dateField: keyof T, from: Date, to: Date): number {
  let n = 0;
  for (const r of rows) {
    const raw = r[dateField] as unknown as string | null | undefined;
    if (!raw) continue;
    const d = new Date(raw);
    if (d >= from && d <= to) n++;
  }
  return n;
}

export function sumInRange<T>(
  rows: T[],
  dateField: keyof T,
  from: Date,
  to: Date,
  amount: (r: T) => number,
): number {
  let total = 0;
  for (const r of rows) {
    const raw = r[dateField] as unknown as string | null | undefined;
    if (!raw) continue;
    const d = new Date(raw);
    if (d >= from && d <= to) total += amount(r) || 0;
  }
  return total;
}

export function pctDelta(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}
